import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Icon, { IconName } from '../../../components/AppIcon';
import {
  BusinessRoleView,
  StrengthAlignmentRowView,
  RequirementStateView,
  SkillHoldLevel,
  EquipmentPriority,
  getRoleFit,
} from '../../../api/General/businessRoles';
import { addUserSkill, editUserSkill, getUserSkills } from '../../../api/Employee/skills';
import { ProficiencyLevel } from '../../../models/employee';
import { Switch } from '../../../components/ui/Switch';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useToast, ToastRenderer } from '../../../hooks/useToast';

/**
 * Coverage below this raises an advisory on assign. Advisory only — it never
 * blocks the assignment, and never becomes an 'error' conflict.
 */
export const POOR_FIT_THRESHOLD = 50;

/**
 * Level presentation. Keyed by the STRING enum member (the API serializes .NET
 * enums as their member name), never by a numeric reverse-map.
 * Every state carries an icon AND a text label so meaning survives without colour.
 * Mirrors SkillRoleGapsSection so both surfaces read identically.
 */
const LEVEL_PRESENTATION: Record<SkillHoldLevel, { icon: IconName; tone: string; labelKey: string; effectKey: string }> = {
  [SkillHoldLevel.Strong]: {
    icon: 'CheckCircle2',
    tone: 'text-success',
    labelKey: 'skillGaps.level.strong',
    effectKey: 'skillGaps.effect.full',
  },
  [SkillHoldLevel.Working]: {
    icon: 'CircleDot',
    tone: 'text-amber-600',
    labelKey: 'skillGaps.level.working',
    effectKey: 'skillGaps.effect.half',
  },
  [SkillHoldLevel.Awareness]: {
    icon: 'Circle',
    tone: 'text-gray-500',
    labelKey: 'skillGaps.level.awareness',
    effectKey: 'skillGaps.effect.quarter',
  },
  [SkillHoldLevel.NotHeld]: {
    icon: 'XCircle',
    tone: 'text-destructive',
    labelKey: 'skillGaps.level.notHeld',
    effectKey: 'skillGaps.effect.zero',
  },
};

const IMPORTANCE_PRESENTATION: Record<EquipmentPriority, { toneClass: string; labelKey: string }> = {
  [EquipmentPriority.Essential]: {
    toneClass: 'bg-red-50 text-red-700 border-red-200',
    labelKey: 'businessRoles.priority.essential',
  },
  [EquipmentPriority.Important]: {
    toneClass: 'bg-amber-50 text-amber-700 border-amber-200',
    labelKey: 'businessRoles.priority.important',
  },
  [EquipmentPriority.Helpful]: {
    toneClass: 'bg-blue-50 text-blue-700 border-blue-200',
    labelKey: 'businessRoles.priority.helpful',
  },
  [EquipmentPriority.Optional]: {
    toneClass: 'bg-gray-100 text-gray-700 border-border',
    labelKey: 'businessRoles.priority.optional',
  },
};

const coverageTone = (percent: number): string => {
  if (percent >= 100) return 'text-success';
  if (percent >= POOR_FIT_THRESHOLD) return 'text-amber-600';
  return 'text-destructive';
};

const coverageIcon = (percent: number): IconName => {
  if (percent >= 100) return 'CheckCircle2';
  if (percent >= POOR_FIT_THRESHOLD) return 'AlertTriangle';
  return 'AlertCircle';
};

interface RoleFitPanelProps {
  role: BusinessRoleView;
  fit: StrengthAlignmentRowView | null;
  loading: boolean;
  employeeId?: string;
  onFitRefreshed?: (fit: StrengthAlignmentRowView) => void;
}

export default function RoleFitPanel({ role, fit, loading, employeeId, onFitRefreshed }: RoleFitPanelProps) {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  // Off by default: the add-skill affordance stays hidden so the advisor reads clean.
  const [editSkills, setEditSkills] = useState(false);
  const [pendingLevel, setPendingLevel] = useState<Record<number, ProficiencyLevel>>({});
  // Skills ticked for adding. Batched deliberately: someone missing four skills used to have
  // to add them one at a time, or leave for the profile screen and lose the assignment.
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);
  const [addingSelected, setAddingSelected] = useState(false);
  // Rows whose add succeeded — hide the control even if the re-score didn't refresh.
  const [addedSkillIds, setAddedSkillIds] = useState<number[]>([]);
  // skillId -> the employee's existing UserSkill. Needed because a requirement row carries no
  // userSkillId, and raising an EXISTING level goes through EditUserSkill: AddUserSkill refuses a
  // duplicate outright ("User already has this skill"), so the add path cannot serve this.
  const [heldSkills, setHeldSkills] = useState<Record<number, { userSkillId: number; proficiency: ProficiencyLevel }>>({});
  const [savingLevelFor, setSavingLevelFor] = useState<number | null>(null);

  const canEditSkills = Boolean(employeeId && onFitRefreshed);

  // Loaded only when the toggle is switched on: the panel reads clean by default and this costs a
  // request nobody needs until they actually intend to edit.
  useEffect(() => {
    if (!editSkills || !employeeId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await getUserSkills(employeeId);
        if (cancelled || result.errors.length > 0 || !result.payload) return;
        const map: Record<number, { userSkillId: number; proficiency: ProficiencyLevel }> = {};
        result.payload.forEach((us) => {
          map[us.skillId] = { userSkillId: us.id, proficiency: us.proficiency as ProficiencyLevel };
        });
        setHeldSkills(map);
      } catch { /* the level control simply stays hidden for rows we cannot resolve */ }
    })();
    return () => { cancelled = true; };
  }, [editSkills, employeeId]);

  /**
   * Change the level of a skill the employee ALREADY holds, then re-score.
   *
   * Applied on change rather than batched: that matches the profile screen's proficiency control,
   * and unlike adding, there is no multi-row workflow to spare the user here.
   */
  const handleChangeLevel = async (skillId: number, next: ProficiencyLevel, requirementName: string) => {
    const held = heldSkills[skillId];
    if (!employeeId || !onFitRefreshed || !held || savingLevelFor != null) return;

    setSavingLevelFor(skillId);
    const previous = held.proficiency;
    // Optimistic so the select does not snap back while the request is in flight; reverted on failure.
    setHeldSkills((prev) => ({ ...prev, [skillId]: { ...prev[skillId], proficiency: next } }));
    try {
      const result = await editUserSkill(held.userSkillId, {
        id: held.userSkillId,
        userId: employeeId,
        skillName: requirementName,
        proficiency: next,
      });
      if (result.errors.length > 0) {
        setHeldSkills((prev) => ({ ...prev, [skillId]: { ...prev[skillId], proficiency: previous } }));
        showToast(t('skills.proficiencyUpdateFailed'), 'error');
        return;
      }
      try {
        const refreshed = await getRoleFit(role.id, employeeId);
        if (refreshed.errors.length === 0) {
          onFitRefreshed(refreshed.payload);
          showToast(t('roleFit.levelUpdated'), 'success');
        } else {
          showToast(t('skills.addedRescoreFailed'), 'warning');
        }
      } catch {
        showToast(t('skills.addedRescoreFailed'), 'warning');
      }
    } catch {
      setHeldSkills((prev) => ({ ...prev, [skillId]: { ...prev[skillId], proficiency: previous } }));
      showToast(t('skills.proficiencyUpdateFailed'), 'error');
    } finally {
      setSavingLevelFor(null);
    }
  };

  const levelOptions = [
    { value: ProficiencyLevel.Awareness, label: t('Awareness') },
    { value: ProficiencyLevel.Working, label: t('Working') },
    { value: ProficiencyLevel.Strong, label: t('Strong') },
  ];

  const toggleSkillSelected = (skillId: number) => {
    setSelectedSkillIds((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId]
    );
  };

  const handleAddSelected = async (missing: RequirementStateView[]) => {
    if (!employeeId || !onFitRefreshed || selectedSkillIds.length === 0) return;
    const chosen = missing.filter((r) => selectedSkillIds.includes(r.skillId));
    if (chosen.length === 0) return;

    setAddingSelected(true);
    try {
      // Stage 1: add each. A failure leaves that skill uncreated, so it stays tickable and
      // re-running is safe. Successes are kept even if a later one fails — partial progress
      // is reported rather than silently rolled back.
      const succeeded: number[] = [];
      for (const requirement of chosen) {
        const level = pendingLevel[requirement.skillId] ?? ProficiencyLevel.Working;
        try {
          const result = await addUserSkill({
            id: 0, userId: employeeId, skillName: requirement.skillName, proficiency: level,
          });
          if (result.errors.length === 0) succeeded.push(requirement.skillId);
        } catch { /* counted as a failure below */ }
      }

      if (succeeded.length === 0) {
        showToast(t('skills.addFailed'), 'error');
        return;
      }

      // Retire the rows that landed so they cannot be re-POSTed, and untick them.
      setAddedSkillIds((prev) => [...prev, ...succeeded.filter((id) => !prev.includes(id))]);
      setSelectedSkillIds((prev) => prev.filter((id) => !succeeded.includes(id)));

      // Stage 2: one re-score for the whole batch — a failure here doesn't undo the adds.
      try {
        const refreshed = await getRoleFit(role.id, employeeId);
        if (refreshed.errors.length === 0) {
          onFitRefreshed(refreshed.payload);
          showToast(
            succeeded.length === chosen.length
              ? t('roleFit.addedCount', { count: succeeded.length })
              : t('roleFit.addedPartial', { added: succeeded.length, total: chosen.length }),
            succeeded.length === chosen.length ? 'success' : 'warning'
          );
        } else {
          showToast(t('skills.addedRescoreFailed'), 'warning');
        }
      } catch {
        showToast(t('skills.addedRescoreFailed'), 'warning');
      }
    } finally {
      setAddingSelected(false);
    }
  };

  if (loading) {
    return (
      <div
        role="status"
        aria-label={t('common.loading')}
        data-testid="fit-panel-loading"
        className="flex items-center justify-center py-6"
      >
        <Icon name="Loader2" size={20} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!fit) return null;

  const coverage = Math.round(fit.coveragePercent);
  const displayName = fit.employeeName ?? fit.employeeId;
  // Missing requirements still open to being added — drives both the tick-boxes and the
  // batch action, so the two can never disagree about what is selectable.
  const selectableMissing = fit.requirements.filter(
    (r) => r.level === SkillHoldLevel.NotHeld && !addedSkillIds.includes(r.skillId)
  );
  // Advisory: below the fit threshold with real requirements to close.
  const isPoorFit = coverage < POOR_FIT_THRESHOLD && fit.requirements.length > 0;
  const responsibilities = role.coreResponsibilities ?? [];
  const showPurpose = Boolean(role.description) || responsibilities.length > 0;

  const renderRequirement = (requirement: RequirementStateView) => {
    const level = LEVEL_PRESENTATION[requirement.level];
    const importance = IMPORTANCE_PRESENTATION[requirement.importance];
    const isMissing = requirement.level === SkillHoldLevel.NotHeld;
    const showAdd = editSkills && canEditSkills && isMissing && !addedSkillIds.includes(requirement.skillId);
    const isSelected = selectedSkillIds.includes(requirement.skillId);
    // A skill the employee already holds is the case that actually drags fit down (held at
    // Awareness against an Essential requirement), and the toggle used to offer nothing for it —
    // the only route was to leave for the profile screen and lose the assignment in progress.
    const held = heldSkills[requirement.skillId];
    const showChangeLevel = editSkills && canEditSkills && !isMissing && Boolean(held);

    return (
      <li
        key={requirement.skillId}
        data-testid={`fit-req-${requirement.skillId}`}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 border-b border-border last:border-b-0"
      >
        {level && <Icon name={level.icon} size={16} className={level.tone} />}
        <span className="text-sm font-medium text-foreground">{requirement.skillName}</span>
        {importance && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${importance.toneClass}`}>
            {t(importance.labelKey)}
          </span>
        )}
        {level && <span className={`text-sm ${level.tone}`}>{t(level.labelKey, { name: displayName })}</span>}
        {level && <span className="text-sm text-muted-foreground">{t(level.effectKey)}</span>}
        {showChangeLevel && (
          <div
            data-testid={`fit-change-level-${requirement.skillId}`}
            className="flex items-center gap-3 w-full mt-1 ps-6"
          >
            <span className="text-xs text-muted-foreground">{t('roleFit.changeLevel')}</span>
            {/* No `disabled` prop: Select is an untyped .jsx component, so any prop set tsc has not
                seen before is a NEW error against scripts/tsc-baseline.json, and that baseline may
                only shrink. `handleChangeLevel` already refuses re-entry while a save is in flight
                (`savingLevelFor != null`), so the guard is functional rather than visual. */}
            <Select
              className="w-40"
              options={levelOptions}
              value={held!.proficiency}
              onChange={(value) => { void handleChangeLevel(requirement.skillId, value as ProficiencyLevel, requirement.skillName); }}
              data-testid={`fit-skill-level-${requirement.skillId}`}
            />
            {/* Saving the level and re-scoring the fit are two round trips; without this the row
                sat unchanged and the user had no idea anything was happening. */}
            {savingLevelFor === requirement.skillId && (
              <span
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                role="status"
                data-testid={`fit-level-saving-${requirement.skillId}`}
              >
                <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
                {t('roleFit.savingLevel')}
              </span>
            )}
          </div>
        )}
        {showAdd && (
          <div
            data-testid={`fit-add-skill-${requirement.skillId}`}
            className="flex items-center gap-3 w-full mt-1 ps-6"
          >
            <Checkbox
              // Explicit id so the visible <label> that actually receives the click has a
              // stable `for` — Checkbox otherwise generates a random one on every render.
              id={`fit-skill-cb-${requirement.skillId}`}
              checked={isSelected}
              onChange={() => toggleSkillSelected(requirement.skillId)}
              disabled={addingSelected}
              aria-label={t('roleFit.selectSkillForAdd', { skill: requirement.skillName })}
              data-testid={`fit-skill-checkbox-${requirement.skillId}`}
            />
            {/* The level only matters once the skill is going to be added, so it appears
                with the tick rather than cluttering every unselected row. */}
            {isSelected && (
              <Select
                className="w-40"
                options={levelOptions}
                value={pendingLevel[requirement.skillId] ?? ProficiencyLevel.Working}
                onChange={(value) =>
                  setPendingLevel((prev) => ({ ...prev, [requirement.skillId]: value as ProficiencyLevel }))
                }
                data-testid={`fit-skill-level-${requirement.skillId}`}
              />
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div data-testid="fit-panel" className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">{t('roleFit.heading')}</h4>
        <div className="flex items-center gap-3">
          {canEditSkills && (
            <div className="flex items-center gap-2">
              <Switch
                checked={editSkills}
                onCheckedChange={setEditSkills}
                id="edit-skills-toggle"
                data-testid="fit-edit-skills-toggle"
              />
              <label htmlFor="edit-skills-toggle" className="text-xs font-medium text-muted-foreground cursor-pointer">
                {t('teamRole.editSkillsWhileAssigning')}
              </label>
            </div>
          )}
          <span
            data-testid="fit-coverage"
            className={`inline-flex items-center text-sm font-medium ${coverageTone(coverage)}`}
          >
            <Icon name={coverageIcon(coverage)} size={14} className="me-1" />
            {coverage}%
          </span>
        </div>
      </div>

      {showPurpose && (
        <div data-testid="fit-purpose">
          <h5 className="text-xs font-medium text-muted-foreground uppercase mb-1">{t('roleFit.purpose')}</h5>
          {role.description && <p className="text-sm text-foreground">{role.description}</p>}
          {responsibilities.length > 0 && (
            <ul className="list-disc ps-5 mt-1 space-y-0.5">
              {responsibilities.map((responsibility, index) => (
                <li key={`${responsibility}-${index}`} className="text-sm text-muted-foreground">
                  {responsibility}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <h5 className="text-xs font-medium text-muted-foreground uppercase mb-1">{t('roleFit.requiredSkills')}</h5>
        {fit.requirements.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('roleFit.noRequirements')}</p>
        ) : (
          <ul>{fit.requirements.map(renderRequirement)}</ul>
        )}

        {/* One action for the whole selection: adding four skills should not mean four
            round-trips, and never means leaving this screen and losing the assignment. */}
        {editSkills && canEditSkills && selectableMissing.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground" data-testid="fit-add-selected-count">
              {t('roleFit.selectedCount', { count: selectedSkillIds.length })}
            </p>
            <Button
              size="sm"
              onClick={() => handleAddSelected(selectableMissing)}
              loading={addingSelected}
              disabled={selectedSkillIds.length === 0 || addingSelected}
              data-testid="fit-add-selected-btn"
            >
              {t('roleFit.addSelected')}
            </Button>
          </div>
        )}
      </div>

      {isPoorFit && (
        <div
          data-testid="fit-gap-hint"
          className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3"
        >
          <Icon name="Lightbulb" size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="text-foreground">{t('roleFit.gapHint')}</p>
            <Link
              to={employeeId ? `/profile/${employeeId}/skills` : '/core-hr/skills-overview'}
              className="mt-1 inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              {t('teamRole.manageFullSkills')}
              <Icon name="ArrowRight" size={14} className="rtl:-scale-x-100" />
            </Link>
          </div>
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
